import React, { useEffect, useState } from 'react';
import { withRouter, Redirect } from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { reduxForm, getFormValues } from 'redux-form/immutable';
import {
  LinearProgress,
  CircularProgress,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
} from '@material-ui/core';

import Box from 'retired/Box';
import { showErrorAction } from 'containers/Notification/actions';
import { PureRender } from 'utils/component-helpers';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import { Features } from 'utils/features';
import {
  selectOrganization,
  selectAccessibleUserLocations,
  selectUser,
} from 'entities/user/selectors';
import { selectBudgets } from 'entities/budget/selectors';
import { selectCategories } from 'entities/cause/selectors';
import Organization from 'entities/organization/model';
import DonationCreditRuleType from 'entities/donationCreditRule/model';
import User from 'entities/user/model';

import reducer from './reducer';
import epics from './epics';
import { selectLoading } from './selectors';
import { FORM_IDENTIFIER } from './constants';
import * as Actions from './actions';
import DonationCreditRule from './DonationCreditRule';
import VolunteerTimeDonationCreditRule from './VolunteerTimeDonationCreditRule';

const key = 'donationCreditRulesFormPage';

interface DonationCreditRulesFormPageProps {
  loading: boolean;
  locations: any;
  categories: any;
  pristine: boolean;
  invalid: boolean;
  submitting: boolean;
  actions: any;
  organization: Organization;
  match: any;
  history: any;
  donationCreditRule: DonationCreditRuleType;
  handleSubmit: (
    submit: (formData: any) => void
  ) => ((event: React.FormEvent<HTMLFormElement>) => void) | undefined;
  budgets: any;
  user: User;
}

const DonationCreditRulesFormPage: React.FC<DonationCreditRulesFormPageProps> = ({
  loading,
  categories,
  pristine,
  invalid,
  submitting,
  actions,
  organization,
  handleSubmit,
  match,
  history,
  donationCreditRule,
  budgets,
  locations,
  user,
}) => {
  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });

  const [selectedRuleType, setSelectedRuleType] = useState('VolunteerTimeDonationCreditRule');

  const newRule = !donationCreditRule?.get('id');

  useEffect(() => {
    actions.initAction(match.params.id, match.params.budgetId);
  }, []);

  useEffect(() => {
    if (!newRule && donationCreditRule) {
      const type = donationCreditRule?.get('type');
      if (selectedRuleType !== type) {
        setSelectedRuleType(type || 'DonationCreditRule');
      }
    }
  }, [newRule, selectedRuleType, donationCreditRule]);

  const handleSelectionChange = (event) => {
    setSelectedRuleType(event.target.value);
  };

  const submit = (formData) => {
    if (pristine) {
      history.replace('/donations/rules');
    } else {
      const formDataJS = formData.toJS();
      formDataJS.type = selectedRuleType;
      actions.saveAction(DonationCreditRuleType.fromFormToRequestJson(formDataJS));
    }
  };

  if (organization && (!organization.isCompany() || !user.hasFeature(Features.DONATION_CREDIT))) {
    return <Redirect to="/donations/rules" replace />;
  }

  const title = 'Donation Credit Rule';

  const budget = budgets?.find(
    (b) => b.id === (donationCreditRule?.budget ?? match.params.budgetId)
  );

  return (
    <>
      {loading && <LinearProgress />}
      {!loading && (
        <Box>
          <Helmet title={title} />
          <h1>{title}</h1>

          {submitting ? (
            <CircularProgress />
          ) : (
            <form onSubmit={handleSubmit(submit)}>
              <FormControl style={{ marginBottom: '20px', width: '200px' }}>
                <InputLabel>Rule Type</InputLabel>
                <Select
                  value={selectedRuleType}
                  onChange={handleSelectionChange}
                  disabled={!newRule}
                >
                  <MenuItem value="VolunteerTimeDonationCreditRule">Volunteering Credit</MenuItem>
                  <MenuItem value="DonationCreditRule">Donation Credit</MenuItem>
                  {selectedRuleType === 'OneTimeDonationCreditRule' && (
                    <MenuItem value="OneTimeDonationCreditRule">Donation Credit</MenuItem>
                  )}
                </Select>
              </FormControl>
              <div>
                {selectedRuleType === 'VolunteerTimeDonationCreditRule' ? (
                  <VolunteerTimeDonationCreditRule
                    donationCreditRule={donationCreditRule}
                    budget={budget}
                    categories={categories}
                    locations={locations}
                    pristine={pristine}
                    invalid={invalid}
                    remove={() => actions.removeAction(match.params.id)}
                    donationCreditRuleId={match.params.id}
                  />
                ) : (
                  <DonationCreditRule
                    donationCreditRule={donationCreditRule}
                    budget={budget}
                    pristine={pristine}
                    invalid={invalid}
                    remove={() => actions.removeAction(match.params.id)}
                    donationCreditRuleId={match.params.id}
                  />
                )}
              </div>
            </form>
          )}
        </Box>
      )}
    </>
  );
};
const getFormValuesSelector = getFormValues(FORM_IDENTIFIER);

const withConnect = connect(
  (state) => ({
    donationCreditRule: getFormValuesSelector(state),
    loading: selectLoading(state),
    organization: selectOrganization(state),
    budgets: selectBudgets(state),
    categories: selectCategories(state),
    locations: selectAccessibleUserLocations(state),
    user: selectUser(state),
  }),
  (dispatch) => ({
    actions: bindActionCreators({ ...Actions, showErrorAction }, dispatch),
  })
);

const withForm = reduxForm({
  form: FORM_IDENTIFIER,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

export default compose(withRouter, withConnect, withForm, PureRender)(DonationCreditRulesFormPage);
